import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plane, Radio, MapPin, Phone, Mail, Clock, Building2 } from "lucide-react";

export default function AirportInfo() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/airport-info").then((r) => setInfo(r.data)); }, []);

  if (!info) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">Loading…</div>;

  const freqs = [
    ["CTAF", info.ctaf],
    ["AWOS", info.awos],
    ["Pattern Alt", `${info.pattern_altitude_ft} ft MSL`],
    ["Elevation", `${info.elevation_ft} ft MSL`],
  ];

  return (
    <div data-testid="airport-info-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Airport Information</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-950 mt-3 tracking-tight">
        {info.name}
      </h1>
      <div className="mt-6 max-w-3xl space-y-5 text-slate-700 text-lg leading-relaxed">
        {(info.description || "").split(/\n\n+/).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <div className="mt-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Field Data</h2>
          <dl className="mt-6 font-mono-data text-sm border-t border-slate-200">
            <Row k="Identifier" v={info.identifier} />
            <Row k="Location" v={`${info.city}, ${info.state}`} />
            <Row k="Runway" v={info.runway} />
            <Row k="Hours" v={info.hours} />
            <Row k="Address" v={info.address} />
          </dl>

          <h2 className="font-display text-2xl font-semibold text-slate-900 mt-12">Frequencies</h2>
          <dl className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-6">
            {freqs.map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 p-5">
                <dt className="text-xs font-mono uppercase tracking-widest text-sky-600">{k}</dt>
                <dd className="font-display text-2xl font-semibold mt-1 text-slate-950 font-mono-data">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <aside className="md:col-span-5 bg-slate-950 text-white p-8">
          <div className="section-label !text-sky-300">FBO Services</div>
          <ul className="mt-6 space-y-3 text-sm">
            {(info.fbo_services || []).map((s) => (
              <li key={s} className="flex items-start gap-3">
                <Plane className="h-4 w-4 text-sky-300 mt-0.5 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>

          <div className="hairline !border-white/10 mt-8 pt-6 space-y-3 text-sm">
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-sky-300" /> {info.phone}</div>
            <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-sky-300" /> {info.email}</div>
            <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-sky-300" /> {info.address}</div>
            <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-sky-300" /> {info.hours}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-3 border-b border-slate-200">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-950 font-medium">{v}</dd>
    </div>
  );
}
